/**
 * Central branding configuration — SINGLE SOURCE OF TRUTH.
 *
 * This file is the one place where the product's public identity is defined:
 *   - platformName / agentName  (currently "Torik" for both — same name for
 *     the platform and the mascot character)
 *   - all public URLs, mailboxes, tagline, and logo paths
 *
 * It is synced into the apps and the hub by `otto-ws/scripts/sync-brand.mjs`:
 *   - otto-ui  -> src/config/brand.ts   (Vue SPA / desktop / touch / play)
 *   - otto-web -> src/lib/brand.ts      (Next.js marketing site + cloud portal)
 *   - otto     -> src/config/brand.ts   (hub server: agent persona, name defaults, labels)
 *
 * NEVER edit the per-app copies directly — edit THIS file and run:
 *   npm run brand:sync      (from otto-ws)
 * Consumers keep importing their existing local path, so no consumer edits
 * are needed when values change.
 *
 * Technical identifiers intentionally do NOT follow this config (that is the
 * separate phase-2 codebase rename): storage keys, integration keys
 * (`otto-touch`), wake-word values (`hey_otto`), mDNS `otto.local`, app
 * bundle ids (`com.ottohome.app`), GitHub org `OttoHomeAI`, repo/package
 * names, MinIO bucket names, product SKUs.
 */
const base = {
  /** Platform brand name, e.g. 'Torik'. */
  platformName: 'Torik',
  /** AI agent / mascot name — same as the platform for the "Torik" brand. */
  agentName: 'Torik',

  /** Short marketing tagline (otto-web). */
  tagline: 'The Spatial Intelligence Platform',

  // ---- Canonical domains ---------------------------------------------------
  // Torik is the single canonical brand domain. The ottohome.ai family (and
  // the never-hosted cloud.ottohome.io) is retired: ottohome.* now 301-
  // redirects to these hostnames.
  siteUrl: 'https://torik.ai',
  /** The live demo house — the public guest walk-in served by the demo hub. */
  demoUrl: 'https://demo.torik.ai/demo',
  appUrl: 'https://app.torik.ai',
  /** Cloud API origin shown in cloud-account UI (otto-ui). */
  cloudUrl: 'https://torik.ai',
  /** Public object storage / firmware & app downloads (MinIO). */
  storageUrl: 'https://storage.torik.ai',

  // ---- Social / links ------------------------------------------------------
  // GitHub org + YouTube handle are still otto-branded until the phase-2
  // codebase/org rename; point these at the new handles when that happens.
  githubUrl: 'https://github.com/OttoHomeAI/otto',
  youtubeUrl: 'https://youtube.com/@TorikSpatial',

  // ---- Mailboxes -----------------------------------------------------------
  // Single Namecheap Private Email mailbox: info@torik.ai. All contact mail
  // routes there until per-role mailboxes (support@, privacy@, ...) are added.
  supportEmail: 'info@torik.ai',
  privacyEmail: 'info@torik.ai',
  legalEmail: 'info@torik.ai',
  dpoEmail: 'info@torik.ai',
  emailFromAddress: 'info@torik.ai',

  // ---- Logos / images ------------------------------------------------------
  // The app icon set is generated from otto-design/Otto/app-icon.html by
  // `npm run icons` (otto-ui) and ships in both apps' public/ root under
  // neutral names (`app-icon-*.png`). Older `Otto*` artwork files remain in
  // public/images for pages that use the original renders (mascot, banners).
  images: {
    /** App icon — headers, login, wake-word indicator (both apps). */
    logoHeader: '/app-icon-192.png',
    /** Logo used on auth pages (otto-web) — app icon at 512 for crisp hero size. */
    logoAuth: '/app-icon-512.png',
    /** Agent mascot image (otto-web auth / register). */
    agent: '/images/Otto.png',
    /** Alternate agent mascot (otto-web banners). */
    agentAlt: '/images/Otto2.png',
    /** Mascot image used as default system/layout background (otto-ui). */
    avatar: '/images/Otto.png',
    favicon: '/favicon.ico',
  },
} as const;

/** Brand object incl. derived helpers (both apps read fields off `brand`). */
export const brand = {
  ...base,
  /** Platform name without spaces — 'Torik'. */
  platformCompact: base.platformName.replace(/\s+/g, ''),
  /** Cloud product name — 'Torik Cloud'. */
  cloudName: `${base.platformName.replace(/\s+/g, '')} Cloud`,
  /** Agent greeting / wake phrase — 'Hey Torik'. */
  heyAgent: `Hey ${base.agentName}`,
  /** Standard page-title suffix helper — `Cart | Torik`. */
  pageTitle: (title: string) => `${title} | ${base.platformName.replace(/\s+/g, '')}`,
} as const;

export const platformCompact = brand.platformCompact;
export const cloudName = brand.cloudName;
export const heyAgent = brand.heyAgent;
export const pageTitle = brand.pageTitle;
